const $$splitNotFoundComponentImporter = () => import('/home/runner/work/nexus/nexus/apps/frontend/src/routes/(app)/route.tsx?tsr-split=notFoundComponent');
const $$splitComponentImporter = () => import('/home/runner/work/nexus/nexus/apps/frontend/src/routes/(app)/route.tsx?tsr-split=component');
import { lazyRouteComponent } from '@tanstack/react-router';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { ldContextFactory } from '@/app';
import { setLogoutHandler, setTokenProvider } from '@/common/api/tempoApi';
import { FullSizeSpinner } from '@/common/components/ui/FullSizeSpinner';
import { permissionsQueryOptions } from '@/common/queries/access/permissionsQueryOptions';
import { getJwtUserContext } from '@/common/utils/getJwtUserContext';
export const Route = createFileRoute('/(app)')({
  component: lazyRouteComponent($$splitComponentImporter, 'component'),
  pendingComponent: FullSizeSpinner,
  notFoundComponent: lazyRouteComponent($$splitNotFoundComponentImporter, 'notFoundComponent'),
  beforeLoad: async ({
    context
  }) => {
    const {
      auth,
      flagOverrides
    } = context;
    if (!auth) {
      throw redirect({
        to: '/'
      });
    }
    const {
      getAccessTokenSilently,
      logout
    } = auth;
    if (!auth.isLoading && !auth.isAuthenticated) {
      if (auth.error?.message.includes('invitation')) {
        throw redirect({
          to: '/organizations/invites/error',
          search: {
            message: auth.error.message
          }
        });
      }
      // The SPA can be unauthenticated while a valid Auth0 session still exists —
      // e.g. returning from a post-login `sendUserTo` interrupt during
      // signup/org-setup, where the code exchange never completed. Recover the
      // session silently off the session cookie before falling back to /login.
      const hasSession = getAccessTokenSilently ? await getAccessTokenSilently().then(() => true).catch(() => false) : false;
      if (!hasSession) {
        throw redirect({
          to: '/login',
          search: {
            returnTo: window.location.href
          }
        });
      }
    }
    let flags = {} as FlagKeys;
    if (logout) {
      setLogoutHandler(() => logout({
        logoutParams: {
          returnTo: window.location.origin
        }
      }));
    }
    try {
      const accessToken = getAccessTokenSilently ? await getAccessTokenSilently() : undefined;
      if (getAccessTokenSilently) {
        setTokenProvider(getAccessTokenSilently, accessToken);
      }
      const authContext = getJwtUserContext(accessToken);
      const {
        userId,
        organizationId,
        organizationName
      } = authContext;
      if (context.ldClient) {
        // identify needs both keys (an empty key throws "Invalid context"); the anonymous
        // flags + overrides below still apply when the token is incomplete.
        if (userId && organizationId) {
          const ldCtx = ldContextFactory.multiContext({
            organizationInput: {
              organizationId,
              organizationName
            },
            userInput: {
              userId
            }
          });
          const currentCtx = context.ldClient.getContext();
          const multiCtx = 'kind' in currentCtx && currentCtx.kind === 'multi' ? currentCtx as LDMultiKindContext : null;
          const contextChanged = !multiCtx || (multiCtx.user as LDContextCommon | undefined)?.key !== userId || (multiCtx.organization as LDContextCommon | undefined)?.key !== organizationId;
          if (contextChanged) {
            try {
              await context.ldClient.identify(ldCtx);
            } catch {
              // A LaunchDarkly identify failure must not break auth/routing.
            }
          }
        }
        const allFlags = context.ldClient.allFlags();
        flags = {
          ...allFlags,
          ...flagOverrides
        } as FlagKeys;
      }
      let permissions: Partial<PermissionsResponseDTO>;
      try {
        permissions = await context.queryClient.fetchQuery(permissionsQueryOptions);
      } catch {
        permissions = {} as Partial<PermissionsResponseDTO>;
      }
      return {
        flags,
        accessToken,
        permissions,
        ...authContext
      };
    } catch {
      return {
        flags,
        accessToken: undefined,
        email: undefined,
        subscription: undefined,
        userId: undefined,
        organizationId: undefined,
        auth0OrganizationId: undefined,
        organizationName: undefined,
        connectionName: undefined,
        authError: true,
        permissions: {} as Partial<PermissionsResponseDTO>
      };
    }
  }
});